/**
 * O piloto controlado (módulo 21; guia §17): o que a mesa e o celular
 * mostram e escrevem, sem rede, para o teste alcançar.
 *
 * A definição e a meta de cada indicador NÃO moram aqui: vêm do servidor
 * (`backend/src/piloto/definicoes.ts`), junto do número. Aqui está só o que é
 * da tela — escrever o valor na unidade certa, o caminho de cada tela que
 * resolve, e o retorno das gravações (R04).
 */
import { formatarDecimal, formatarDia, formatarInteiro } from '@/lib/formato';
import type { RetornoDeclarado } from '@/lib/retorno';

/* ----------------------------------------------------------------- tipos */

export type Unidade = 'porcento' | 'horas' | 'segundos' | 'contagem';

export interface ValorDoPiloto {
  readonly valor: number | null;
  readonly base: string;
  readonly porQue: string | null;
  readonly naMeta: boolean | null;
}

export interface MetaDoPiloto {
  readonly valor: number;
  readonly sentido: 'minimo' | 'maximo';
  readonly texto: string;
  readonly fonte: string;
}

export interface MedidaDoPiloto {
  readonly id: string;
  readonly rotulo: string;
  readonly unidade: Unidade;
  readonly definicao: string;
  readonly meta: MetaDoPiloto;
  readonly grupo: ValorDoPiloto;
  readonly empresa: ValorDoPiloto;
  readonly extra?: { readonly perfisDoGrupoSemUso?: readonly string[]; readonly semInstante?: number; readonly semInstanteNoGrupo?: number };
}

export type IdDoIndicador = 'cadastro' | 'prazo' | 'acuracidade' | 'preventivas' | 'tempos' | 'ia' | 'adocao';

export interface IndicadorDoPiloto {
  readonly id: IdDoIndicador;
  readonly titulo: string;
  readonly pergunta: string;
  readonly medidas: readonly MedidaDoPiloto[];
}

export interface PerfilAtivo {
  readonly perfil: string | null;
  readonly pessoas: number;
  readonly ativas: number;
  readonly pessoasNoGrupo: number;
  readonly ativasNoGrupo: number;
}

export interface PainelDoPiloto {
  readonly dias: number;
  readonly de: string;
  readonly ate: string;
  readonly geradoEm: string;
  readonly prazoDoRegistroH: number;
  readonly grupo: { readonly maquinas: number; readonly maquinasHoje: number; readonly pessoas: number; readonly pessoasHoje: number };
  readonly indicadores: readonly IndicadorDoPiloto[];
  readonly porPerfil: readonly PerfilAtivo[];
}

export type Onde = 'ficha' | 'biblioteca' | 'manutencao' | 'tanques' | 'estoque' | 'governanca' | 'custos';

export interface TipoDePendencia {
  readonly id: string;
  readonly rotulo: string;
  readonly oQueFazer: string;
  readonly onde: Onde;
}

export interface MaquinaDaCarga {
  readonly id: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly modelo: string | null;
  readonly noPiloto: boolean;
  readonly pendencias: readonly string[];
}

export interface PendenciaDaEmpresa extends TipoDePendencia {
  readonly itens: readonly { readonly id: string; readonly codigo: string | null; readonly nome: string }[];
}

export interface CargaInicial {
  readonly hoje: string;
  readonly grupo: 'piloto' | 'todas';
  readonly tipos: Readonly<Record<string, TipoDePendencia>>;
  readonly resumo: {
    readonly maquinas: number;
    readonly completas: number;
    readonly porPendencia: Readonly<Record<string, number>>;
    readonly noPiloto: number;
    readonly completasNoPiloto: number;
  };
  readonly itens: readonly MaquinaDaCarga[];
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly empresa: readonly PendenciaDaEmpresa[];
}

export interface PassagemDaMaquina {
  readonly id: string;
  readonly equipamentoId: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly modelo: string | null;
  readonly entrada: string;
  readonly saida: string | null;
  readonly noGrupo: boolean;
  readonly vencidasNaEntrada: number | null;
  readonly observacao: string;
  readonly registradoPor: string | null;
}

export interface PassagemDaPessoa {
  readonly id: string;
  readonly usuarioId: string;
  readonly nome: string;
  readonly email: string | null;
  readonly perfil: string | null;
  readonly entrada: string;
  readonly saida: string | null;
  readonly noGrupo: boolean;
  readonly observacao: string;
  readonly registradoPor: string | null;
}

export interface GrupoDoPiloto {
  readonly hoje: string;
  readonly maquinas: readonly PassagemDaMaquina[];
  readonly pessoas: readonly PassagemDaPessoa[];
}

export interface Candidato {
  readonly id: string;
  readonly codigo?: string;
  readonly descricao?: string;
  readonly modelo?: string | null;
  readonly nome?: string;
  readonly email?: string | null;
  readonly perfil?: string | null;
  readonly noGrupo: boolean;
}

export type TipoDoGrupo = 'maquina' | 'pessoa';

export interface EntradaNoPiloto {
  readonly tipo: TipoDoGrupo;
  readonly id: string;
  readonly entrada: string;
  readonly observacao: string;
}

export interface RespostaDaEntrada {
  readonly id: string;
  readonly codigo?: string;
  readonly descricao?: string;
  readonly nome?: string;
  readonly entrada: string;
  readonly vencidasNaEntrada?: number | null;
  readonly maquinasNoGrupo?: number;
  readonly pessoasNoGrupo?: number;
}

export interface SaidaDoPiloto {
  readonly tipo: TipoDoGrupo;
  readonly id: string;
  readonly saida: string;
}

export interface RespostaDaSaida {
  readonly id: string;
  readonly quem: string;
  readonly entrada: string;
  readonly saida: string;
  readonly noGrupo: number;
}

/* --------------------------------------------------------------- a busca */

export const PERIODOS_DO_PILOTO = [7, 30, 90] as const;
export type PeriodoDoPiloto = (typeof PERIODOS_DO_PILOTO)[number];
export type AbaDoPiloto = 'indicadores' | 'carga' | 'grupo';

export interface BuscaDoPiloto {
  readonly aba?: 'carga' | 'grupo';
  readonly dias?: PeriodoDoPiloto;
  readonly grupo?: 'piloto';
  readonly pendencia?: string;
  readonly pagina?: number;
}

/** O estado no endereço (p.16): o padrão (indicadores, 30 dias, todas, página 1) não aparece. */
export function validarBuscaDoPiloto(entrada: Record<string, unknown>): BuscaDoPiloto {
  const aba = entrada['aba'];
  const dias = Number(entrada['dias']);
  const pendencia = entrada['pendencia'];
  const pagina = Number(entrada['pagina']);
  return {
    ...(aba === 'carga' || aba === 'grupo' ? { aba } : {}),
    ...((PERIODOS_DO_PILOTO as readonly number[]).includes(dias) && dias !== 30 ? { dias: dias as PeriodoDoPiloto } : {}),
    ...(entrada['grupo'] === 'piloto' ? { grupo: 'piloto' as const } : {}),
    ...(typeof pendencia === 'string' && /^[a-z_]{3,40}$/.test(pendencia) ? { pendencia } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
  };
}

/* ------------------------------------------------------- o valor escrito */

/**
 * O número na unidade da medida (p.61): `85%`, `3,5 h`, `12 s`, `4`. Sem
 * número, o travessão — e a tela escreve o porquê ao lado, nunca zero.
 */
export function escreverValor(v: ValorDoPiloto, unidade: Unidade): string {
  if (v.valor === null) return '—';
  switch (unidade) {
    case 'porcento':
      return `${formatarInteiro(Math.round(v.valor * 100))}%`;
    case 'horas':
      return `${formatarDecimal(v.valor, 1)} h`;
    case 'segundos':
      return `${formatarDecimal(v.valor, 1)} s`;
    case 'contagem':
      return formatarInteiro(v.valor);
  }
}

/** A linha de contexto do valor: a base ("de 42 turnos") ou o porquê do travessão. */
export function contextoDoValor(v: ValorDoPiloto): string {
  return v.valor === null ? (v.porQue ?? 'Sem dado no período.') : v.base;
}

/** "Últimos 30 dias · 08/09/2026 a 07/10/2026" */
export function periodoEscrito(p: Pick<PainelDoPiloto, 'dias' | 'de' | 'ate'>): string {
  return `Últimos ${String(p.dias)} dias · ${formatarDia(p.de)} a ${formatarDia(p.ate)}`;
}

/** "3 máquinas e 5 pessoas no piloto hoje" — e o grupo vazio dito como vazio. */
export function grupoEscrito(g: PainelDoPiloto['grupo']): string {
  if (g.maquinasHoje === 0 && g.pessoasHoje === 0) return 'O grupo do piloto está vazio hoje';
  const m = `${formatarInteiro(g.maquinasHoje)} ${g.maquinasHoje === 1 ? 'máquina' : 'máquinas'}`;
  const p = `${formatarInteiro(g.pessoasHoje)} ${g.pessoasHoje === 1 ? 'pessoa' : 'pessoas'}`;
  return `${m} e ${p} no piloto hoje`;
}

/* -------------------------------------------- as telas que resolvem */

export interface DestinoDaMesa {
  readonly to: '/equipamentos' | '/tecnico' | '/manutencao' | '/combustivel/tanques' | '/estoque' | '/cadastros/governanca' | '/custos';
  readonly search?: Record<string, string>;
  readonly rotulo: string;
}

/**
 * ONDE A PENDÊNCIA SE RESOLVE, na mesa. A ficha abre pela frota filtrada pelo
 * código: a mesa não tem endereço próprio para a ficha (ela é o painel de
 * detalhe da lista).
 */
export function destinoNaMesa(onde: Onde, maquina?: Pick<MaquinaDaCarga, 'codigo'>): DestinoDaMesa {
  switch (onde) {
    case 'ficha':
      return { to: '/equipamentos', ...(maquina ? { search: { termo: maquina.codigo } } : {}), rotulo: 'Abrir a ficha' };
    case 'biblioteca':
      return { to: '/tecnico', rotulo: 'Abrir a Biblioteca técnica' };
    case 'manutencao':
      return { to: '/manutencao', rotulo: 'Abrir Manutenção' };
    case 'tanques':
      return { to: '/combustivel/tanques', rotulo: 'Abrir os tanques' };
    case 'estoque':
      return { to: '/estoque', rotulo: 'Abrir o Estoque' };
    case 'governanca':
      return { to: '/cadastros/governanca', rotulo: 'Abrir Perfis e responsáveis' };
    case 'custos':
      return { to: '/custos', rotulo: 'Abrir Custos' };
  }
}

export interface DestinoNoCelular {
  readonly to: '/frota/$equipamentoId/editar' | '/biblioteca' | '/manutencao/planos' | '/combustivel' | '/estoque' | '/admin/pessoas' | '/custos';
  readonly params?: Record<string, string>;
  readonly rotulo: string;
}

/** A mesma pergunta, no celular: lá a ficha tem endereço, e a edição também. */
export function destinoNoCelular(onde: Onde, maquina?: Pick<MaquinaDaCarga, 'id'>): DestinoNoCelular {
  switch (onde) {
    case 'ficha':
      return { to: '/frota/$equipamentoId/editar', ...(maquina ? { params: { equipamentoId: maquina.id } } : {}), rotulo: 'Editar a ficha' };
    case 'biblioteca':
      return { to: '/biblioteca', rotulo: 'Abrir a Biblioteca técnica' };
    case 'manutencao':
      return { to: '/manutencao/planos', rotulo: 'Abrir os planos' };
    case 'tanques':
      return { to: '/combustivel', rotulo: 'Abrir o combustível' };
    case 'estoque':
      return { to: '/estoque', rotulo: 'Abrir o estoque' };
    case 'governanca':
      return { to: '/admin/pessoas', rotulo: 'Abrir as pessoas' };
    case 'custos':
      return { to: '/custos', rotulo: 'Abrir Custos' };
  }
}

export interface TelaDaMedida {
  readonly mesa: { readonly to: '/painel/piloto' | '/operacao' | '/combustivel' | '/operacao/conciliacao' | '/estoque' | '/combustivel/tanques' | '/manutencao' | '/ordens' | '/painel/ia' | '/combustivel/cobrancas'; readonly search?: Record<string, string> };
  readonly celular: '/operacao' | '/combustivel' | '/operacao/conciliacao' | '/estoque' | '/manutencao' | '/oficina' | '/ia' | '/combustivel/cobrancas' | null;
  readonly rotulo: string;
}

/**
 * ONDE ESTÃO OS REGISTROS de cada medida (§9 do guia: "chegar aos registros
 * que formam o indicador"). O celular não tem a carga inicial nem o grupo em
 * outra tela: nesses, nulo — o próprio painel é a tela.
 */
export const TELA_DA_MEDIDA: Readonly<Record<string, TelaDaMedida>> = {
  cadastro_completo: { mesa: { to: '/painel/piloto', search: { aba: 'carga' } }, celular: null, rotulo: 'Ver a carga inicial' },
  apontamento_no_prazo: { mesa: { to: '/operacao' }, celular: '/operacao', rotulo: 'Ver os turnos' },
  abastecimento_no_prazo: { mesa: { to: '/combustivel' }, celular: '/combustivel', rotulo: 'Ver os abastecimentos' },
  horas_sem_divergencia: { mesa: { to: '/operacao/conciliacao' }, celular: '/operacao/conciliacao', rotulo: 'Ver a conciliação de horas' },
  contagens_sem_ajuste: { mesa: { to: '/estoque' }, celular: '/estoque', rotulo: 'Ver o estoque' },
  medicoes_na_tolerancia: { mesa: { to: '/combustivel/tanques' }, celular: '/combustivel', rotulo: 'Ver os tanques' },
  preventiva_no_prazo: { mesa: { to: '/manutencao' }, celular: '/manutencao', rotulo: 'Ver a manutenção' },
  preventivas_vencidas: { mesa: { to: '/manutencao' }, celular: '/manutencao', rotulo: 'Ver as vencidas' },
  tempo_para_abrir_os: { mesa: { to: '/ordens' }, celular: '/oficina', rotulo: 'Ver as O.S.' },
  tempo_da_resposta_tecnica: { mesa: { to: '/painel/ia' }, celular: '/ia', rotulo: 'Ver o registro da IA' },
  ia_util: { mesa: { to: '/painel/ia' }, celular: '/ia', rotulo: 'Ver a qualidade da IA' },
  ia_citada: { mesa: { to: '/painel/ia' }, celular: '/ia', rotulo: 'Ver a qualidade da IA' },
  ia_erro_critico: { mesa: { to: '/painel/ia', search: { aba: 'avaliacao' } }, celular: '/ia', rotulo: 'Ver o conjunto de avaliação' },
  pessoas_ativas: { mesa: { to: '/painel/piloto', search: { aba: 'grupo' } }, celular: null, rotulo: 'Ver o grupo' },
  fluxo_os: { mesa: { to: '/ordens' }, celular: '/oficina', rotulo: 'Ver as O.S.' },
  fluxo_preventiva: { mesa: { to: '/manutencao' }, celular: '/manutencao', rotulo: 'Ver a manutenção' },
  fluxo_abastecimento: { mesa: { to: '/combustivel/cobrancas' }, celular: '/combustivel/cobrancas', rotulo: 'Ver as cobranças' },
  fluxo_turno: { mesa: { to: '/operacao' }, celular: '/operacao', rotulo: 'Ver os turnos' },
};

/* ------------------------------------------------- o retorno das gravações */

/**
 * O que o toast da entrada diz além do título (R04): o alvo, a data, o
 * retrato da entrada e o grupo antes → depois. O TÍTULO é declarado em cada
 * gravação ("Máquina posta no piloto"), como o teste do R04 cobra.
 */
export function retornoDaEntrada(r: RespostaDaEntrada, e: EntradaNoPiloto): Omit<RetornoDeclarado, 'titulo'> {
  const maquina = e.tipo === 'maquina';
  const depois = (maquina ? r.maquinasNoGrupo : r.pessoasNoGrupo) ?? 0;
  /* Por DATA, e não por instante: entrar hoje às 9 h já é estar no grupo. */
  const entraHoje = r.entrada <= hojeNoCampo();
  return {
    alvo: maquina ? { codigo: r.codigo ?? '', nome: r.descricao ?? '' } : { nome: r.nome ?? '' },
    fatos: [
      `entrada em ${formatarDia(r.entrada)}`,
      maquina
        ? r.vencidasNaEntrada === null || r.vencidasNaEntrada === undefined
          ? 'sem plano preventivo na entrada'
          : `${formatarInteiro(r.vencidasNaEntrada)} ${r.vencidasNaEntrada === 1 ? 'preventiva vencida' : 'preventivas vencidas'} na entrada`
        : null,
      e.observacao.trim() ? e.observacao.trim() : null,
    ],
    efeito: entraHoje
      ? { rotulo: maquina ? 'Máquinas no piloto hoje' : 'Pessoas no piloto hoje', antes: formatarInteiro(Math.max(0, depois - 1)), depois: formatarInteiro(depois) }
      : `Entra no grupo em ${formatarDia(r.entrada)}; até lá, os indicadores não a contam`,
  };
}

/** O toast da saída além do título: o último dia, a entrada e o grupo antes → depois. */
export function retornoDaSaida(r: RespostaDaSaida, s: SaidaDoPiloto): Omit<RetornoDeclarado, 'titulo'> {
  const maquina = s.tipo === 'maquina';
  /* O último dia ainda é do grupo: só sai da conta de hoje quem saiu antes. */
  const saiuJa = r.saida < hojeNoCampo();
  return {
    alvo: maquina ? { codigo: r.quem } : { nome: r.quem },
    fatos: [`último dia no piloto: ${formatarDia(r.saida)}`, `entrou em ${formatarDia(r.entrada)}`],
    efeito: saiuJa
      ? { rotulo: maquina ? 'Máquinas no piloto hoje' : 'Pessoas no piloto hoje', antes: formatarInteiro(r.noGrupo + 1), depois: formatarInteiro(r.noGrupo) }
      : 'Os registros até o último dia continuam contando para o piloto',
  };
}

/** A data de hoje no formato do campo (aaaa-mm-dd), no fuso de quem usa. */
export function hojeNoCampo(agora: Date = new Date()): string {
  const d = agora;
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** O que falta no formulário de entrada, ao tentar gravar (p.23: o erro diz o que fazer). */
export function faltaNaEntrada(e: Pick<EntradaNoPiloto, 'tipo' | 'id' | 'entrada'>): Partial<Record<'id' | 'entrada', string>> {
  return {
    ...(e.id ? {} : { id: e.tipo === 'maquina' ? 'Escolha a máquina na lista.' : 'Escolha a pessoa na lista.' }),
    ...(/^\d{4}-\d{2}-\d{2}$/.test(e.entrada) ? {} : { entrada: 'Informe a data de entrada no piloto.' }),
  };
}
